/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {getSelectedFilters} from '@ion/dls/src/filters/SelectedFilters';
import {getFilterSelection} from '@ion/dls/src/filters/utils';
import {
  findNearbyIsActiveSelector,
  findNearbyV2IsActiveSelector,
  trackingFiltersSelector
} from '../../../../store/selectors/tracking-selectors';

export const toggleFilterStyle = (manaul: string, auto: string) =>
  ({isFilterSelected}: {isFilterSelected: boolean}) => isFilterSelected ? manaul : auto
;

/**
 * The useIsFilterSelected hook is used to identify whether a filter is selected
 * or not in the Tracking page.
 *
 * @returns true if any filter selected in Tracking page
 *
 * ```typescript
 *
 *   import {useIsFilterSelected} from './hook';
 *
 *   const isFilterSelected = useIsFilterSelected();
 *
 * ```
 */
export const useIsFilterSelected = (): boolean => {
  const trackingFilters = useSelector(trackingFiltersSelector);
  const findNearbyIsActive = useSelector(findNearbyIsActiveSelector);
  const findNearbyV2IsActive = useSelector(findNearbyV2IsActiveSelector);
  
  const isFilterSelected = useMemo(() => {
    const selectedFilters = getSelectedFilters(trackingFilters);
    const {selectedCount} = getFilterSelection(selectedFilters);
    return !!selectedCount || !!trackingFilters.boundary.length || !!findNearbyIsActive || !!findNearbyV2IsActive;
  }, [trackingFilters, findNearbyIsActive, findNearbyV2IsActive]);

  return isFilterSelected;
};

export default useIsFilterSelected;
